/**
 * Camp page distance pill
 *
 * Shows "X.X mi away", consistent with the Algolia search card: if the search
 * shows a distance for a camp, the camp page shows it too. The ONLY difference
 * is the cap — the search uses a 250-mile radius, the camp page never shows
 * distance over RADIUS_MILES (30). There is NO state gate.
 *
 * Reference center:
 *  - campSearchCenter (localStorage, set by the search = its exact aroundLatLng):
 *    the same center the search card measured from → identical number. Stored in
 *    localStorage (not sessionStorage) so it carries to the camp page regardless
 *    of tab; the search overwrites it on every search.
 *  - no campSearchCenter (user never searched): realLocationData (real IP) —
 *    "how far is this camp from where you are".
 *
 * Camp coords (data-camp-lat/lng = entry.map) == Algolia _geoloc; same Haversine
 * (CampsUtils.calculateDistance, R=6378137m — matches Algolia's geo distance),
 * /1609, toFixed(1) as the card.
 * Pill visibility is gated by the AB class on <html> (ab-camp-distance-v2).
 */

const RADIUS_MILES = 30;
const METERS_PER_MILE = 1609;

async function initCampPageDistance() {
  const pills = document.querySelectorAll(
    ".rd-camp-header__distance-pill[data-camp-lat], .rd-camp-brand-hero__distance-pill[data-camp-lat]"
  );
  if (!pills.length) return;

  // AB test WITH the pill → render the pill(s) and report their actual visibility.
  // AB test WITHOUT the pill (control / feature off) → a distance is never shown → false.
  // The AB gate stays on the RENDER call: with the test OFF the pill JS must NOT un-hide
  // the pill / set data-has-distance (that would flip the location layout even though the
  // pill stays display:none). When the test is off we still report false to analytics.
  const abActive = document.documentElement.classList.contains(
    "ab-camp-distance-v2"
  );
  const distanceShown = abActive
    ? await resolveAndRenderDistancePills(pills)
    : false;

  // Push to the GTM dataLayer whether a distance was shown on this camp page, mirroring
  // the pill's final visibility: true = "X.X mi away" rendered; false = no distance
  // (no center, non-US direct visit, or beyond the 30-mile cap). Scoped to the AB test
  // (same gate as the pill) so it fires only while the experiment is active.
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "camp_distance_shown",
      distance_shown: !!distanceShown,
    });
  } catch (e) {
    /* non-fatal: analytics push must never break the page */
  }
}

/**
 * Resolve the reference center and render the distance pill(s).
 * @param {NodeListOf<Element>} pills
 * @returns {Promise<boolean>} true iff at least one pill was shown ("X.X mi away")
 */
async function resolveAndRenderDistancePills(pills) {
  const utils = window.CampsUtils;
  if (!utils || !utils.ensureGeolocationData || !utils.calculateDistance) return false;

  await utils.ensureGeolocationData();

  let cached;
  try {
    cached = JSON.parse(localStorage.getItem(utils.defaults.cacheKey) || "null");
  } catch (e) {
    return false;
  }
  if (!cached) return false;

  // Reference center — always show the SAME number the search showed:
  //  1. campSearchCenter (localStorage): the EXACT aroundLatLng the last search
  //     measured from → guaranteed identical to the search card. Persisted by the
  //     search on every query (algolia-search.js ~5260 / search-executor.js ~72),
  //     survives navigation/re-entry, removed when the search had no center. Trusted
  //     with NO isUS gate — this is what lets a non-US IP that SEARCHED a US location
  //     still get the pill (the distance the search already showed).
  //  2. realLocationData (real IP): only when there is NO search center (true direct
  //     visit, never searched). US visitors only — a non-US direct visitor gets no pill.
  // `coords` is intentionally NOT used: it can diverge from the card (state search
  // centers on the user's IP / nothing, while coords holds the state centroid), which
  // would break the "identical numbers" rule. campSearchCenter mirrors aroundLatLng.
  let sc = null;
  try {
    sc = JSON.parse(localStorage.getItem("campSearchCenter") || "null");
  } catch (e) {
    sc = null;
  }
  const fromSearch = !!(sc && sc.lat != null && sc.lng != null);

  const coords = cached.coords; // diagnostic only
  const geo = cached.realLocationData;

  let center, mode;
  if (fromSearch) {
    center = { lat: sc.lat, lng: sc.lng };
    mode = "fromSearch (campSearchCenter)";
  } else if (geo && geo.isUS === true && geo.lat != null && geo.lng != null) {
    center = { lat: geo.lat, lng: geo.lng };
    mode = "directVisit (realLocationData)";
  } else {
    // Non-US IP and no prior search → no distance.
    return false;
  }

  // ── TEMP DIAGNOSTIC (ab_camp_distance) — remove once verified ──
  pills.forEach((pill) => {
    const cLat = parseFloat(pill.dataset.campLat);
    const cLng = parseFloat(pill.dataset.campLng);
    if (isNaN(cLat) || isNaN(cLng)) return;
    const camp = { lat: cLat, lng: cLng };
    const mi = (c) =>
      c && c.lat != null && c.lng != null
        ? (Math.round(utils.calculateDistance({ lat: c.lat, lng: c.lng }, camp)) / METERS_PER_MILE).toFixed(1)
        : "n/a";
    console.log("📏 [ab_camp_distance] camp-page distance debug", {
      campCoords: camp,
      mode,
      center,
      userLocation: cached.userLocation || null,
      searchCenter: sc || null,
      cachedCoords: coords
        ? { lat: coords.lat, lng: coords.lng, isUS: coords.isUS }
        : null,
      realLocationData: geo
        ? { lat: geo.lat, lng: geo.lng, isUS: geo.isUS, state: geo.regionCode || geo.region }
        : null,
      distance_used_mi: mi(center),
      distanceFrom_searchCenter_mi: mi(sc),
      distanceFrom_coords_mi: mi(coords),
      distanceFrom_realLocationData_mi: mi(geo),
    });
  });

  let anyShown = false;
  pills.forEach((pill) => {
    const campLat = parseFloat(pill.dataset.campLat);
    const campLng = parseFloat(pill.dataset.campLng);
    if (isNaN(campLat) || isNaN(campLng)) return;

    // Round meters to a whole number BEFORE converting, so the pill rounds the
    // same way Algolia's geoDistance does (it arrives pre-rounded to integer
    // meters). Without this, the card feeds integer meters and the pill feeds
    // float meters into the same /1609 → toFixed(1); at a 0.05-mile boundary the
    // sub-meter gap flips the last digit (e.g. card "4.4" vs pill "4.5").
    const miles = parseFloat(
      (Math.round(utils.calculateDistance(center, { lat: campLat, lng: campLng })) / METERS_PER_MILE).toFixed(1)
    );
    // Only difference from the search: the 30-mile cap (search uses a 250-mile radius).
    if (miles > RADIUS_MILES) return;

    pill.textContent = `${miles.toFixed(1)} mi away`;
    pill.removeAttribute("hidden");
    pill.dataset.visible = "true";

    const container = pill.closest(
      ".rd-camp-header__location, .rd-camp-brand-hero__location"
    );
    if (container) container.dataset.hasDistance = "true";

    anyShown = true;
  });

  return anyShown;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCampPageDistance);
} else {
  initCampPageDistance();
}
